import { 
  CONNECTOR_MASTER_REQUEST_PROTOCOLS, 
  GATE_CONNECTOR_REQUEST_PROTOCOLS, 
  GATE_TO_CONNECTOR_PROTOCOLS, 
  LEAVE_AREA_REASONS, 
  PROTOCOLS,
  safeMerge,
  getLogger,
  bufferToBase64,
  unframeRawWithArea,
} from "@gotti/core";

import type {
  GameId,
  GameType,
  ReceivedMessage,
  AssetAction,
  AssetChange,
} from "@gotti/core";

import { BaseServer, BaseServerConfig, RegisteredServerData } from "../BaseServer.js";
import { BaseServerConnector, ServerClientConnector, ProtocolArray } from "../ServerConnector/BaseServerConnector.js";
import { WebSocketServerConnector } from "../ServerConnector/WebSocketServerConnector.js";
import { WebRTCServerConnector } from "../ServerConnector/WebRTCServerConnector.js";

// Commented out unused type
// type Game = {
//     id: string,
//     type: string,
// }

export type ReservedSeat<AuthT=any, SeatOptionsT=any> = {
  publicId: string | number,
  privateId: string,
  gameId: string | number,
  // The gate resolves this from the master's game registry when it reserves the
  // seat. It lets the connector accept a seat for a game whose areas are all
  // dynamic instances (created AFTER the seat), so it can't yet be in the
  // connector's area-populated `games` map.
  gameType?: string | number,
  auth?: AuthT,
  options?: SeatOptionsT,
  /**
   * Areas this client holds a seat in, derived by the GATE from server state.
   * Never client-supplied — a client cannot name an area it has no seat in.
   */
  rejoinAreaIds?: Array<string | number>,
}

import { ServerOptions } from 'ws';
import { AreaServerConfigOptions } from "./AreaServer.js";
import { MASTER_CONNECTOR_PROTOCOLS, SERVER_SERVER_PROTOCOLS, SERVER_TYPES } from "../constants.js";
import { UDPServerConnector } from "../ServerConnector/UDPServerConnector.js";
import type { RtcConfig } from "node-datachannel";

export type ConnectorServerConfigOptions = {
    maxClients: number,
    websocketOptions?: ServerOptions,
    webRtcOptions?: RtcConfig & { maxAck?: number },
    udpOptions?: {
        port: number,
        host: string,
    },
    protocolType?: 'websocket' | 'udp' | 'webRtc',
    pingInterval?: number,
    clientTimeout?: number,
    messageDebounceRate?: number,
    areas?: (string | number)[],
}

export type ConnectorServerConfig = Omit<BaseServerConfig<ConnectorServerConfigOptions>, 'type'>
const { GATE, MASTER, CONNECTOR, AREA } = SERVER_TYPES;

export class ConnectorServer extends BaseServer {
    readonly logger = getLogger("ConnectorServer:", { kind: 'connector', serverId: this.id });

    private enqueueTimeout : NodeJS.Timer;
    readonly games : {[id: string]: { type: string, areaCount: number }}= {};
    readonly master : Array<BaseServerConfig<any>> = [];
    readonly connector : BaseServerConnector<unknown>;
    readonly reservedSeats : {[seatId: string]: ReservedSeat } = {};
    readonly clientAreas : {[clientId: string]: Array<string | number> } = {};
    readonly batchedServerIds : (string | number)[] = [];
    readonly clientsByArea : {[areaId: string]: Array<string> } = {}
    readonly areaChannels : {[areaId: string]: {[channelIndex: number]: Array<string>}} = {};
    declare readonly config: BaseServerConfig<ConnectorServerConfigOptions>;

    readonly connectedAreas : {[areaId: string]: RegisteredServerData<AreaServerConfigOptions> } = {};

    // private curReq : number = 0;
    readonly pendingRequests : {[req: number]: { callback: (v: any) => void, clientId: string }} = {};
    
    private gateServer : BaseServerConfig<any>;
    private masterServer : BaseServerConfig<any>;

    /**
     * Set at the top of close() so the AREA-disconnect handler — which close()
     * itself triggers when it disconnects each area peer — knows the CONNECTOR
     * ITSELF is going away and must NOT emit client-facing CLIENT_LEFT_AREA.
     * See disconnectAllClientsInArea / close() for the full rationale.
     */
    private closing = false;

    /**
     * privateIds that sent PROTOCOLS.CLIENT_LEAVING — a DELIBERATE leave
     * (client.close()/clearGame()) — moments before their socket closes. The
     * socket-close handler (handleRemovedClient) consumes the mark to relay an
     * immediate EVICT to the client's areas and a deliberate-purge to the gate,
     * instead of the grace-hold/detach path reserved for abnormal drops (where a
     * reconnect may follow). Deleted on consumption (or with the client record),
     * so a marked-but-not-yet-closed socket never leaks the flag.
     */
    private readonly deliberateLeavers: Set<string> = new Set();

    /*
    readonly batchedMessagesByArea : {
        [area: string]: {[system: string]: {[protocol: string]: {[messageType: string]: Array<{ payload: any, clientId: string }> }
    }}} = {};
    */
    readonly batchedMessagesByArea : {
        [area: string]: Array<any>
    } = {};


    /**
     * The last area of a game went away — every client seated in that game has
     * nothing left to talk to; drop their connections.
     */
    async disconnectAllClientsInGame(gameId: string) {
        Object.values(this.connector.clientsByPrivateId).forEach(client => {
            if(client.seat?.gameId === gameId) {
                this.connector.removeClient(client.privateId);
            }
        });
    }

    /**
     * An area's connection dropped (e.g. a dynamic instance was destroyed or its
     * host was lost) — pull every client out of it and tell them they left.
     *
     * EXCEPTION — the connector's OWN shutdown: close() disconnects each area
     * peer, which fires this via the AREA-close handler. In that case we must
     * NOT emit the client-facing CLIENT_LEFT_AREA (guarded by `closing`), so a
     * reconnecting client replays its FULL area set instead of an emptied one.
     */
    async disconnectAllClientsInArea(areaId: string | number) {
        const clientIds = [...(this.clientsByArea[areaId] ?? [])];
        clientIds.forEach(clientId => {
            // When the CONNECTOR ITSELF is shutting down (close() set `closing`),
            // do NOT tell clients they "left the area": their socket is about to
            // drop and the reconnect must restore their FULL area set. Sending
            // CLIENT_LEFT_AREA over the still-live socket races the client into
            // handleLeftArea (emptying joinedAreasById, tearing down that area's
            // client-side state) BEFORE handleConnectionLost — so even though the
            // gate's held-area list still routes it back (see the report split
            // below), the client has already forgotten it was there: the rebind
            // arrives as a FRESH join, re-initialising the area file and losing
            // the per-area state the grace window exists to preserve. The AREA
            // side already handles the connector's disappearance via its own
            // detach path. A genuine area
            // death (the area crashes / a dynamic instance is destroyed while
            // the connector stays alive) keeps `closing` false, so clients ARE
            // still notified — the legitimate use of this method.
            //
            // The same `closing` split governs the GATE report: while `closing`
            // this connector is going away wholesale (every area, every client),
            // which is the same "membership is HELD, not ended" shape as the
            // disconnect teardown — report:false so the gate's list survives for
            // the client's next connector to inherit. A genuine single-area death
            // with `closing` false really does end membership in THIS area while
            // the client's other areas live on, so the gate is told (default).
            this.removeClientFromArea(clientId, areaId, { report: !this.closing });
            if (!this.closing) {
                this.connector.sendToClient(clientId, [
                    PROTOCOLS.CLIENT_LEFT_AREA,
                    areaId,
                    LEAVE_AREA_REASONS.REMOVED_BY_AREA,
                    {},
                ], true, true);
            }
        });
    }
  
    /**
     * TURN CREDENTIALS ARE SECRETS AND THEY LIVE IN `options`.
     *
     * `webRtcOptions` is an `RtcConfig`, and node-datachannel's `IceServer` and
     * `ProxyServer` both carry `username`/`password` (a TURN relay is a metered,
     * authenticated service — those credentials are worth stealing). An ice
     * server may also be given as a bare URL string with the credentials inline
     * (`turn:user:pass@host:3478`), so both forms are handled.
     *
     * Without this they ride verbatim on the connector's registration over the
     * same unauthenticated `tcp://` the redis credential used to, and
     * `Master.broadcastConnectorChange({op:'added', config: s})` then RE-BROADCASTS
     * the registered record to every area. Pre-existing — this is not a leak the
     * optional-redis work introduced — but it is the reason `sharedConfig` grew a
     * per-type hook rather than just dropping one top-level key: `options` is
     * where server types keep their secrets, and only the type that owns it knows
     * which parts those are.
     *
     * SHAPE-PRESERVING, deliberately. `GateServer` uses `'webRtcOptions' in
     * reserved.connector.options` as its protocol DISCRIMINATOR (then takes
     * host/port from `websocketOptions`), so deleting the key would silently turn
     * every webRtc connector into a websocket one at seat-reservation time. The
     * key stays; only the credential fields inside it go. Nothing anywhere reads
     * a PEER's ice servers — grepped: `WebRTCServerConnector` reads
     * `this.config.options.webRtcOptions`, its own.
     */
    protected override redactSharedOptions(options: ConnectorServerConfigOptions): ConnectorServerConfigOptions {
        const webRtcOptions = options?.webRtcOptions;
        if(!webRtcOptions) {
            return options;
        }
        const redacted: any = { ...webRtcOptions };
        if(Array.isArray(webRtcOptions.iceServers)) {
            redacted.iceServers = webRtcOptions.iceServers.map((server: any) => {
                if(typeof server === 'string') {
                    // Strip an inline `user:pass@` userinfo segment, keeping the
                    // scheme and host so the entry still names the same server.
                    return server.replace(/^([a-zA-Z][a-zA-Z0-9+.-]*:)(?:[^@/]*@)/, '$1');
                }
                const { username, password, ...rest } = server ?? {};
                return rest;
            });
        }
        if(webRtcOptions.proxyServer) {
            const { username, password, ...rest } = webRtcOptions.proxyServer as any;
            redacted.proxyServer = rest;
        }
        return { ...options, webRtcOptions: redacted };
    }

    constructor(c : ConnectorServerConfig) {
        super({ ...c, type: CONNECTOR });
         if (c.options.webRtcOptions) {
            this.connector = new WebRTCServerConnector(c);
            // console.log('made connector')
        } else if(c.options.websocketOptions) {
            // console.log('CONSTRUCTED WEBSOCKET CONNECTOR!!', c)
            this.connector = new WebSocketServerConnector(c);
        } else if (c.options.udpOptions) {
            // console.log('MAKING UDP SERVER CONNECTOR')
            this.connector = new UDPServerConnector(c);
        } else {
            throw new Error(`Connector needs websocket, webRtc, or udp config options.`)
        }

        this.onConnectionMade(d => {
            // console.log('ON CONNECTION MADE:', d.type);
            if(d.type === GATE) {
              this.gateServer = d;
              this.registerClientValidation();
            } else if (d.type === AREA) {
                this.connectedAreas[d.id] = d;
                // Only track game counts for non-global areas (those with a gameId)
                if(d.options.gameId !== undefined && d.options.gameId !== null) {
                    if(!(d.options.gameId in this.games)) {
                        // the area being added IS the game's first area
                        this.games[d.options.gameId] = { type: d.options.gameFile?.type, areaCount: 1 };
                    } else {
                        this.games[d.options.gameId].areaCount++;
                    }
                }
            } else if (d.type === MASTER) {
                this.masterServer = d;
            }
        });

        this.onConnectionClosed(d => {
          if(d.type === AREA) {
            if(d.id in this.connectedAreas) {
              delete this.connectedAreas[d.id];
            }
            if (this.areaChannels[d.id]) {
                delete this.areaChannels[d.id];
            }
            this.disconnectAllClientsInArea(d.id);
            // Only track game counts for non-global areas (those with a gameId)
            if(d.options.gameId !== undefined && d.options.gameId !== null && d.options.gameId in this.games) {
              this.games[d.options.gameId].areaCount--;
              if(!this.games[d.options.gameId].areaCount) {
                delete this.games[d.options.gameId]
                // An INSTANCE area vanishing does NOT mean the game is gone — a
                // fresh instance can be spun up on demand — so seated clients
                // (e.g. a lobby / matchmaking client between rooms) must survive.
                // Only a STATIC area's disappearance (the game genuinely has no
                // areas left) evicts the game's clients.
                if(!d.options.isDynamic) {
                  this.disconnectAllClientsInGame(d.options.gameId)
                }
              }
            }
          }
        });

        this.connector.onWebClientMessage((client, parsed: ProtocolArray) => this.handleClientProtocolMessage(client, parsed));

        this.connector.onRawClientMessage((client, buffer) => this.handleClientRawFrame(client, buffer));

        this.connector.onJoinedClient(async client => {
          // console.log('ON JOINED CLIENT', client.publicId);
          const seat = this.reservedSeats[client.privateId];
          delete this.reservedSeats[client.privateId];
          if(seat) {
            client.seat = seat;
            this.sendToServer(this.gateServer.id, [GATE_TO_CONNECTOR_PROTOCOLS.FINISHED_RESERVATION, client.privateId]);
            return true;
          }
          return false;
        });
        this.connector.onRemovedClient(client => this.handleRemovedClient(client));
    }

    /**
     * A client's socket closed. Relay its departure to every area it was in and
     * report the disconnect to the gate.
     *
     * If the client sent PROTOCOLS.CLIENT_LEAVING first — a DELIBERATE
     * close()/clearGame() — the mark on `deliberateLeavers` is consumed here and
     * threaded through as a `deliberate` flag: the area evicts immediately
     * (handleClientLeave) and the gate purges auth now. Without a prior
     * CLIENT_LEAVING (an abnormal drop / crash) `deliberate` is false and the
     * area DETACHES + the gate GRACE-HOLDS, giving the client the reconnect
     * window. The relayed area reason stays DISCONNECTED in BOTH cases — a
     * deliberate close still leaves channels with LEAVE_CHANNEL_REASONS.DISCONNECTED
     * and fires onClientLeft(DISCONNECTED), exactly as before failover; the
     * separate flag — not a reason change — is what flips detach→evict /
     * grace→purge. Consuming the mark (Set.delete) also cleans it up so it can't
     * leak past the socket that set it.
     */
    private handleRemovedClient(client: ServerClientConnector<unknown>) {
        // console.log('on removed client...', this.clientAreas, client.privateId)
        const deliberate = this.deliberateLeavers.delete(client.privateId);
        // Reject all pending requests for this client
        if(client.pendingReqs) {
          for(const seq in client.pendingReqs) {
            try {
              client.pendingReqs[seq](null, 'Client disconnected');
            } catch (e) {
              // Ignore errors from already-rejected promises
            }
          }
        }
        if(client.privateId in this.clientAreas) {
          this.removeBatchedMessagesForClient(client.privateId);
          const clientAreas = this.clientAreas[client.privateId];
          delete this.clientAreas[client.privateId]
          clientAreas.forEach(areaId => {
            this.removeClientFromArea(client.privateId, areaId, { report: false })
            this.sendToServer(areaId, [
              PROTOCOLS.CLIENT_LEFT_AREA,
              client.publicId,
              LEAVE_AREA_REASONS.DISCONNECTED,
              0,
              deliberate,
            ])
          });
        }
        this.sendToServer(this.gateServer.id, [GATE_TO_CONNECTOR_PROTOCOLS.CLIENT_DISCONNECTED, client.privateId, deliberate]);
        delete this.reservedSeats[client.privateId];
    }

    public handleClientToAreaRequest(clientId: string, seq: number, toArea: string, toSystem: number | string, msgType: number | string, payload: any) {
        if(this.clientAreas[clientId]?.includes(toArea)) {
          const publicId = this.connector.clientsByPrivateId[clientId]?.publicId;
            // OMIT the trailing `to` slot when the client omitted it (never send
            // null/undefined over the wire) — the area routes `=== undefined` to
            // the area file's onClientGameRequest.
            const areaRequest: ProtocolArray = [PROTOCOLS.CLIENT_TO_SERVER_REQUEST, msgType, payload, publicId];
            if (toSystem !== undefined) areaRequest.push(toSystem);
            this.requestServer(toArea, areaRequest).then((d) => {
                this.connector.sendToClient(clientId, [PROTOCOLS.CLIENT_TO_SERVER_REQUEST, seq, d], true, true);
            }).catch(err => {
                this.connector.sendToClient(clientId, [PROTOCOLS.CLIENT_TO_SERVER_REQUEST, seq, null, err.message], true, true)
            });
        } else {
            this.connector.sendToClient(clientId, [PROTOCOLS.CLIENT_TO_SERVER_REQUEST, seq, null, 'Client was not in area.'])
        }
    }

    public override onMasterBroadcast(message: ReceivedMessage) {
			this.connector.broadcastAll(JSON.stringify([PROTOCOLS.MASTER_GLOBAL_BROADCAST, message.type, message.data]), true, true);
    }

    /** Master says a client asset changed → push it to every connected client. */
    public broadcastAssetChangeToClients(action: AssetAction, change: AssetChange): void {
        this.connector.broadcastAll(JSON.stringify([PROTOCOLS.ASSET_CHANGED, action, change]), true, true);
    }


    /**
     * Every protocol frame a connected game client sends. Extracted from the
     * `onWebClientMessage` closure so the routing branches — especially the
     * direct-to-area path every un-addressed system send now takes — are
     * directly testable.
     */
    public handleClientProtocolMessage(client: ServerClientConnector<unknown>, parsed: ProtocolArray) {
          const protocol : PROTOCOLS = parsed[0];
          switch(protocol) {
            case PROTOCOLS.PEER_SIGNAL_REQUEST: {
              // this.relayMessageToPlayer(parsed[1], PROTOCOLS.PEER_SIGNAL_REQUEST, [client.publicId, parsed[2]]);
              this.handlePeerSignalRequest(client.publicId, parsed[1], parsed[2]) 
              break;
            }
            case PROTOCOLS.PEER_CONNECTION_REQUEST: {
              this.logger.info('PEER CONNECTION REQUEST to system: ' + parsed[3])
              this.handlePeerConnectionRequest(client, parsed[1], parsed[2], parsed[3], parsed[4]);
              break;
            }
            case PROTOCOLS.CLIENT_TO_SERVER_MESSAGE_INSTANT: {
              const msgType = parsed[1];
              const payload = parsed[2];
           //   //console.log('client public id was', client.publicId, 'private id was', client.privateId)
              if(parsed.length > 3) {
                const toSystem = parsed[3];
                this.sendToServers(this.clientAreas[client.privateId], [
                  PROTOCOLS.CLIENT_TO_SERVER_MESSAGE,
                  client.publicId, 
                  msgType, 
                  payload,
                  toSystem,
                ])
              } else {
                this.sendToServers(this.clientAreas[client.privateId], [
                  PROTOCOLS.CLIENT_TO_SERVER_MESSAGE,
                  client.publicId, 
                  msgType, 
                  payload,
                ])
              }
              break;
            }
            case PROTOCOLS.CLIENT_TO_SERVER_REQUEST: {
                const seq = parsed[1];
                const toArea = parsed[2];
                const msgType = parsed[3];
                const payload = parsed[4];
                const toSystem = parsed[5];
                this.handleClientToAreaRequest(client.privateId, seq, toArea, toSystem, msgType, payload);
                break;
            }
            case PROTOCOLS.CLIENT_TO_SERVER_MESSAGE: {
                //console.log('PARSED WAS', parsed)
                const msgType = parsed[1];
                const payload = parsed[2];

                if(parsed.length > 3) {
                    const toSystem = parsed[3];
                    //console.log('enque to server', client.privateId)
                    //console.log('client areas was: ', this.clientAreas[client.privateId])
                    this.clientAreas[client.privateId]?.forEach(area => {
                        //console.log('to area', area);
                        this.sendLocalOrEnqueueToServer(area, [
                            PROTOCOLS.CLIENT_TO_SERVER_MESSAGE,
                            client.publicId, 
                            msgType, 
                            payload,
                            toSystem, 
                        ]);
                    })
                } else {
                    this.clientAreas[client.privateId]?.forEach(area => {
                        //console.log('to area', area);
                        this.sendLocalOrEnqueueToServer(area, [
                            PROTOCOLS.CLIENT_TO_SERVER_MESSAGE,
                            client.publicId, 
                            msgType, 
                            payload,
                        ]);
                    })
                }

                break;
            }
      case PROTOCOLS.CLIENT_TO_SERVER_RAW_MESSAGE: {
        const encodedPayload = parsed[1];
        // Optional trailing slot: the area the sending system resolved to.
        const toArea = parsed.length > 2 ? String(parsed[2]) : undefined;
        if(typeof encodedPayload === 'string') {
          this.handleClientRawPayload(client, encodedPayload, toArea);
        }
        break;
      }
            case PROTOCOLS.CLIENT_TO_DIRECT_AREA_SERVER_MESSAGE:
            case PROTOCOLS.CLIENT_TO_DIRECT_AREA_SERVER_MESSAGE_INSTANT: {
                const msgType = parsed[1];
                const payload = parsed[2];
                const toServer = parsed[3]
                const toSystem = parsed[4];
                // Every un-addressed client system send now arrives here with a
                // resolved area (see @gotti/client areaRouting), so this is the
                // hot path — a client may only direct-send to an area it is
                // actually in.
                if(!this.isClientInArea(client.privateId, toServer)) {
                    this.logger.error(`Dropping client message to area: ${toServer} — client ${client.publicId} is not in that area.`);
                    break;
                }
                const areaMessage = [
                    PROTOCOLS.CLIENT_TO_SERVER_MESSAGE,
                    client.publicId,
                    msgType,
                    payload,
                    toSystem,
                ];
                // INSTANT bypasses the outbound queue, same as the
                // un-addressed CLIENT_TO_SERVER_MESSAGE_INSTANT above.
                if(parsed[0] === PROTOCOLS.CLIENT_TO_DIRECT_AREA_SERVER_MESSAGE_INSTANT) {
                    this.sendToServers([toServer], areaMessage);
                } else {
                    this.sendLocalOrEnqueueToServer(toServer, areaMessage);
                }
                break;
            }
            case PROTOCOLS.CONNECTOR_REQUEST: {
                const seq = parsed[1];
                const res = parsed[2];
                const err = parsed[3];
                this.connector.clientsByPrivateId[client.privateId]?.pendingReqs?.[seq]?.(res, err);
                break;
            }
            case PROTOCOLS.PEER_SIGNAL_FAILED: {
                this.relayMessageToPlayer(parsed[1], PROTOCOLS.PEER_SIGNAL_FAILED, [parsed[2]]);
                break;
            }
            case PROTOCOLS.REQUEST_JOIN_AREA: {
                // A refused/failed join must REJECT the client's joinArea, not hang
                // it — reply with the error in the CLIENT_JOINED_AREA error slot.
                this.handleRequestJoinArea(client, parsed[1], parsed[2]).catch(err => {
                    this.connector.sendToClient(client.privateId, [
                        PROTOCOLS.CLIENT_JOINED_AREA,
                        parsed[1], null, null, null, null, false,
                        err instanceof Error ? err.message : String(err),
                    ], true, true);
                });
                break;
            }
            case PROTOCOLS.GET_FIRST_AREAS: {
                //console.log('handling GET_FIRST_AREAS', parsed[1], client.seat?.options);
                this.handleGetFirstAreas(client, parsed[1]);
                break;
            }
            case PROTOCOLS.CLIENT_LEAVING: {
                // Deliberate teardown (client.close()/clearGame()): mark the
                // impending disconnect intentional so handleRemovedClient — which
                // fires when the socket close follows moments later — relays an
                // immediate evict to areas + a deliberate purge to the gate,
                // rather than the grace path an abnormal drop takes.
                this.deliberateLeavers.add(client.privateId);
                break;
            }
            case PROTOCOLS.GAME_MESSAGE: {
                const msgType = parsed[1];
                const payload = parsed[2];
                if(parsed.length > 3) {
                    if(this.clientsByArea[client.privateId].includes(parsed[3])) {
                        this.sendLocalOrEnqueueToServer(parsed[3], [PROTOCOLS.GAME_MESSAGE, msgType, payload]);
                    }
                } else {
                    this.clientAreas[client.privateId]?.forEach(area => {
                        //console.log('to area', area);
                        this.sendLocalOrEnqueueToServer(area, [
                            PROTOCOLS.CLIENT_TO_SERVER_MESSAGE,
                            client.publicId, 
                            msgType, 
                            payload
                        ]);
                    })
                }
                //console.log('sending to area', { toSystem, msgType, payload });
                
            }
          }
    }

    /**
     * A raw binary frame from a client. Binary transports carry no protocol
     * envelope, so the target area rides in the frame header (always written
     * by the client — see frameRawWithArea).
     */
    public handleClientRawFrame(client: ServerClientConnector<unknown>, buffer: ArrayBuffer) {
        const { areaId, data } = unframeRawWithArea(buffer);
        this.handleClientRawPayload(client, bufferToBase64(data), areaId);
    }

  /** Is this client currently a member of `areaId`? */
  private isClientInArea(clientPrivateId: string, areaId: string | number): boolean {
    return !!this.clientAreas[clientPrivateId]?.includes(areaId);
  }

  /**
   * `areaId` present → the sending system resolved to exactly one area (see
   * @gotti/client areaRouting) and only that area gets the frame. Absent → the
   * client is in no single area for this system, so every area it is in gets it,
   * as raw messages always did.
   */
  private handleClientRawPayload(client: ServerClientConnector<unknown>, encodedPayload: string, areaId?: string) {
    const clientAreas = this.clientAreas[client.privateId];
    if(!clientAreas?.length) {
      return;
    }
    if(areaId !== undefined) {
      if(!this.isClientInArea(client.privateId, areaId)) {
        this.logger.error(`Dropping raw client message to area: ${areaId} — client ${client.publicId} is not in that area.`);
        return;
      }
      this.sendLocalOrEnqueueToServer(areaId, [
        PROTOCOLS.CLIENT_TO_SERVER_RAW_MESSAGE,
        client.publicId,
        encodedPayload,
      ]);
      return;
    }
    clientAreas.forEach(area => {
      this.sendLocalOrEnqueueToServer(area, [
        PROTOCOLS.CLIENT_TO_SERVER_RAW_MESSAGE,
        client.publicId,
        encodedPayload,
      ]);
    });
  }

    public async handlePeerConnectionRequest(
        requester: ServerClientConnector<unknown>,
        requestedPublicId: string,
        handshakeData: { sdp?: string, candidate?: string }, 
        systemName?: string,
        requestOptions?: any
    ) {
        try {
            const { success, response, error } = await this.requestServer(this.gateServer.id, [
                GATE_CONNECTOR_REQUEST_PROTOCOLS.PEER_CONNECTION_REQUEST,
                requester.publicId,
                requestedPublicId,
                handshakeData, 
                systemName, 
                requestOptions
            ]);
            //console.log({ success, response, error })
            if(!success) {
                throw new Error(error || `Peer failed to accept request.`);
            } else {
                //console.log('SENDING PEER CONNECTION REQUEST SUCCESS')
                this.connector.sendToClient(requester.privateId, [PROTOCOLS.PEER_CONNECTION_REQUEST_SUCCESS, requestedPublicId, response], true, true)
            }
        } catch (err) {
            this.connector.sendToClient(requester.privateId, [PROTOCOLS.PEER_CONNECTION_REQUEST_FAILED, requestedPublicId, err.message], true, true)
        } 
    }
    
    private relayMessageToPlayer(toPlayerPublicId: string, protocol: PROTOCOLS, msg: any) {
			this.sendToServer(this.gateServer.id, [GATE_TO_CONNECTOR_PROTOCOLS.REDIRECT_MESSAGE_TO_CLIENT, toPlayerPublicId, protocol, msg])
    }

    public async handlePeerSignalRequest(signalFromPlayerPublicId: string, sendingToPublicId: string, handshakeData: { sdp?: string, candidate?: string }) {
			// console.log('handlePeerSignalRequest', { signalFromPlayerPublicId, sendingToPublicId })
			this.relayMessageToPlayer(sendingToPublicId, PROTOCOLS.PEER_SIGNAL_REQUEST, [signalFromPlayerPublicId, handshakeData])
    }

    /**
     * ONE comparison for every id scheme: `String(seat.publicId) === String(publicId)`.
     *
     * This used to fork on `useSequentialPublicIds` and compare
     * `seat.publicId === Number.parseInt(publicId, 10)` for the sequential
     * scheme. Both halves of that were wrong:
     *
     *  - IT REJECTED VALID CLIENTS. `publicId` arrives off the wire (a websocket
     *    query param), so it is always a STRING, while `seat.publicId` is
     *    whatever the gate's record carries. On a REDIS-backed gate the record
     *    comes back out of a hash, where every value is a string, so the seat
     *    carried `"1"` and `"1" === 1` is false — every handshake was rejected
     *    and the client retried `joinGame` forever against a seat this connector
     *    already held. `servers.redis` + `useSequentialPublicIds` could not seat
     *    a single client.
     *  - IT ACCEPTED INVALID ONES. `parseInt` stops at the first non-digit and
     *    skips leading whitespace: `parseInt('1abc', 10)`, `parseInt(' 1', 10)`
     *    and `parseInt('1.9', 10)` are all `1`, so seat 1 admitted a socket
     *    presenting any of those. `String() === String()` is STRICTLY STRICTER
     *    — it accepts exactly one spelling of the id and nothing else.
     *
     * For the default (string-id) scheme this is identical to the comparison it
     * replaces: both sides were already strings. The fork existed only because
     * of the type difference, and normalizing both sides removes the reason for
     * it — which is also why the gate's own publicId comparisons (`resumeSession`'s
     * abandoned-vs-held check, the ownership index on both stores) are written
     * the same way.
     */
    private registerClientValidation() {
      if(!this.gateServer) {
          throw new Error(`No gate server connected to register client validation.`);
      }
      this.connector.onValidateClient((privateId: string, publicId: string) => {
        const seat = this.reservedSeats[privateId];
        if(!seat) {
            return false;
        }
        return String(seat.publicId) === String(publicId);
      });
    }

    /**
     * Drop `rejoin`/`rejoinAreaIds` from options a CLIENT sent. Both are
     * server-derived: the gate sets the flag on the seat (areas read it in
     * `onClientConnected` — and in `onJoinRequest` only where that hook still
     * runs, i.e. a FIRST join, since an area holding a seat rebinds without
     * consulting it) and derives the held-area list from its own client→area
     * index. A client may still send them — an old build, or a hand-rolled one
     * probing for an opening — so every place client options meet seat options
     * strips them first, rather than letting safeMerge carry them onward. The
     * SEAT's own copies are untouched: they live on `client.seat.options`, which
     * no client can write.
     */
    private static stripClientRejoinAssertions(options?: any) {
        if(options && typeof options === "object" && !Array.isArray(options)) {
            delete (options as any).rejoinAreaIds;
            delete (options as any).rejoin;
        }
        return options;
    }

    private async handleRequestJoinArea(client: ServerClientConnector<unknown>, areaId: string, options?: any) {
        if(!client.seat) throw new Error(`No seat on client...`);
        if(!(areaId in this.connectedAreas)) {
            throw new Error(`Area ${areaId} was not connected.`)
        }
        // A forged `rejoin: true` here would reach the area's onJoinRequest and
        // tell it this is a reconnect when it is a first join. The area decides
        // rejoin-vs-fresh off its OWN detachedClients set, so nothing downstream
        // is corrupted — but game code reads the flag, so it must be the
        // server's, never the client's.
        ConnectorServer.stripClientRejoinAssertions(options);
        this.sendToServer(areaId, [PROTOCOLS.REQUEST_JOIN_AREA, client.publicId, client.seat.auth, safeMerge(client.seat.options, options || {})]);
    }

    private async handleGetFirstAreas(client: ServerClientConnector<unknown>, options?: {[key: string]: any }) {
        try {
           // console.log('handleGetFirstAreas:', client.privateId)
            if(client.gotFirstAreas) {throw new Error(`Already got first areas...`);}
            if(!client.seat) throw new Error(`No seat on client...`);
            client.gotFirstAreas = true;
            //console.log('requesting server...', this.masterServer.id);
            
            // console.log('rewquesting serber...')
            // The client cannot assert its own rejoin: master routes off the
            // SEAT's list (sent as its own slot below), and the seat's own
            // `rejoin` flag survives the merge that follows.
            ConnectorServer.stripClientRejoinAssertions(options);
            // Merge the SEAT's options in UNCONDITIONALLY — a non-object payload
            // (string/number/null from a hand-rolled client) must not be able to
            // skip it. Skipping used to be self-defeating (master needed the
            // merged `rejoin`/`rejoinAreaIds` too, so suppressing them cost the
            // client its routing); now that routing comes off the seat's own
            // slot, a skipped merge would be pure gain for an attacker: full
            // rejoin routing into every held area while the game sees no
            // `rejoin`/`previousConnectorId` at all — so every hook and system
            // that reads the flag (onClientConnected, onJoinRequest on a FIRST
            // join) would be told a reconnect is a fresh join and reset the
            // player's state. (An area holding a seat rebinds unconditionally
            // and never consults onJoinRequest, so this is about the flag's
            // VISIBILITY, not about an admission decision.) The client's own
            // options are still honoured whenever it sends an object.
            options = safeMerge(client.seat.options, options && typeof options === "object" ? options : {});
            const requestOptions = options;

            const areaIds = await this.requestServer(this.masterServer.id, [
                CONNECTOR_MASTER_REQUEST_PROTOCOLS.FINISHED_RESERVATION_GET_INITIAL_AREAS,
                client.privateId,
                client.seat.gameId,
                client.seat.auth,
                options,
                client.seat.rejoinAreaIds,
            ]);
            //console.log('AREA IDS ', areaIds);
            const joiningAreaIds : ({ areaId: string | number, options: any })[] = [];
            for(let i = 0; i < areaIds.length; i++) {
                const area = areaIds[i];
                if(typeof area === 'object') {
                    if(!('areaId' in area)) {
                        throw new Error(`Expected areaId in area if it's an object.`);
                    }
                    const { areaId, options: areaOptions } = area;
                    joiningAreaIds.push({ areaId, options: safeMerge(requestOptions, areaOptions) });
                } else {
                    joiningAreaIds.push({ areaId: area, options: requestOptions });
                 }
            }
            if(!joiningAreaIds.length) {
                throw new Error(`Fialed to find any valid area ids to join.`)
            }
            //console.log('send to client', client.privateId)
            this.connector.sendToClient(client.privateId,  [PROTOCOLS.GET_FIRST_AREAS, true, ...joiningAreaIds.map(a => a.areaId)], true, true)
            for(let i = 0; i < joiningAreaIds.length; i++) {
                const { areaId, options: areaOptions } = joiningAreaIds[i];
                this.sendToServer(areaId as string, [
                    PROTOCOLS.CLIENT_JOINED_AREA, 
                    client.publicId, 
                    client.seat.auth,
                    areaOptions
                ])
            }


        } catch (err: any) {
            this.connector.sendToClient(client.privateId,  [PROTOCOLS.GET_FIRST_AREAS, false, err.message], true, true);
        }
    }

    /**
     * Graceful runtime removal, step 1 (Starter.removeConnectorAtRuntime): push
     * every currently-connected client a SERVER_REQUESTED_RECONNECT nudge —
     * BaseClientConnector.onServerMessage routes it straight into the client's
     * own reconnect driver, which re-gates it onto a DIFFERENT connector (the
     * gate already excluded this one via the 'draining' CONNECTOR_CHANGE, Task
     * 7) — then poll until this connector is empty or drainMs elapses,
     * whichever comes first. A straggler still connected when drainMs runs out
     * is left for the caller's subsequent link-teardown/close() to clean up.
     */
    public async beginDrain(drainMs: number): Promise<void> {
        for (const client of [...this.connector.clients]) {
            this.connector.sendToClient(client.privateId, [PROTOCOLS.SERVER_REQUESTED_RECONNECT], true, true);
        }
        const start = Date.now();
        while (this.connector.clients.length > 0 && Date.now() - start < drainMs) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }

    public override async close() {
        // Flag the self-shutdown BEFORE disconnecting peers: disconnectServer on
        // each area synchronously fires this connector's AREA-close handler
        // (disconnectAllClientsInArea), which must stay silent toward clients
        // while we're closing — see disconnectAllClientsInArea's rationale.
        this.closing = true;
        // A connector going away must tell its peers (master / gate / areas) that
        // it is gone BEFORE tearing down its sockets, so they run their
        // onConnectionClosed(CONNECTOR) path immediately — gate grace-holds the
        // affected auth records, the area DETACHES (not evicts) its clients, and
        // the master fans CONNECTOR_CHANGE — instead of waiting out the
        // ~15-30s heartbeat timeout. Without this, a hard close() (crash-style,
        // no drain) lets the client — which detects its own socket death
        // instantly — race back through the gate and be seated as a BRAND-NEW
        // join before the area ever detached it: onClientJoined re-fires, the
        // per-player state seeded there is wiped, the dead connector lingers in
        // the area's broadcast set, and the client's uplink never re-binds.
        // For co-located peers (the `gotti dev` / startAsHost topology, and every
        // in-process integration test) disconnectServer removes the reciprocal
        // link synchronously, so the detach happens before the socket even
        // closes — no race. It is idempotent (guards on current membership), so
        // the graceful removeConnectorAtRuntime drain path — which already
        // disconnected these peers — is a safe no-op here.
        for (const peer of Object.values(this.serverConnections)) {
            try {
                await this.disconnectServer(peer);
            } catch (err) {
                // best-effort: a peer already torn down (or a remote pubSub hiccup
                // mid-shutdown) must never block this connector's own close.
            }
        }
        await Promise.all([
            super.close(),
            this.connector?.close()
        ])
    }

    private removeBatchedMessagesForClient(privateId: string) {
        const ids = [...this.batchedServerIds];
        ids.forEach(serverId => {
            const m = this.batchedMessagesByArea[serverId];
            for (let i = m.length - 1; i >= 0; i--) {
                if (m[i][0] === privateId) {
                    m.splice(i, 1);
                }
            }
            if(!m.length) {
                delete this.batchedMessagesByArea[serverId];
                const idx = this.batchedServerIds.indexOf(serverId);
                if(idx > -1) {
                    this.batchedServerIds.splice(idx, 1);
                }
            }
        })
    }

    public override onHeartbeat() {
        return { 
            clientCount: this.connector.clients.length + Object.keys(this.reservedSeats).length,
            areas: Object.keys(this.clientsByArea),
            maxClients: this.config.options.maxClients 
        }
    }
    
    /*
    public sendEnqueued() {

        const protocolToIndex = {
            [PROTOCOLS.CLIENT_TO_SERVER_MESSAGE]: 0,
            [PROTOCOLS.CLIENT_TO_SERVER_REQUEST]: 1,
            [PROTOCOLS.SERVER_TO_CLIENT_REQUEST]: 2
        }

        this.batchedServerIds.forEach(serverId => {
            const finalArray = [[], [], []];
            const bactchedProtocols = this.batchedMessagesByArea[serverId];
            const protocols = Object.keys(bactchedProtocols);
            protocols.forEach(p => {
                const finalPayload = finalArray[protocolToIndex[p]];
                const batchedSystemMessages = bactchedProtocols[p];
                const systems = Object.keys(bactchedProtocols[p]);
                systems.forEach(s => {
                    const systemPayload : Array<any> = [s];
                    const batchedTypedMessages = batchedSystemMessages[s];
                    const types = Object.keys(batchedTypedMessages);
                    types.forEach(m => {
                        const typeArray = [m];
                        const payloadsForType = batchedTypedMessages[m];
                        payloadsForType.forEach(p => {
                            typeArray.push(p.clientId, p.payload);
                        });
                        systemPayload.push(typeArray);
                    });
                    finalPayload.push(systemPayload);
                });
            });
            this.sendToServer(serverId, finalArray);
            delete this.batchedMessagesByArea[serverId];
        });
        this.batchedServerIds.length = 0;
    }*/
    public sendLocalOrEnqueueToServer(serverId: string | number, payload: Array<any>) {
        //console.log('sending to server: ', serverId)
        const localServer = this.localServers[serverId];
        if(localServer) {
            //console.log('was local: ', serverId)
            localServer.onServerMessage(this.sharedConfig, payload);
        } else {
            if(!(serverId in this.batchedMessagesByArea)) {
                //console.log('init batch ', serverId)
                this.batchedMessagesByArea[serverId] = [SERVER_SERVER_PROTOCOLS.IS_MULTIPLE_MESSAGES, this.id, []];
                this.batchedServerIds.push(serverId);
            }
            this.batchedMessagesByArea[serverId][2].push(payload);
            if(!this.enqueueTimeout) {
                this.enqueueTimeout = setTimeout(() => {
                    this.enqueueTimeout = null;
                    //console.log('finally send enqueued!')
                    this.sendEnqueued();
                }, this.config.options?.messageDebounceRate || 100);
            }
        }
    }
    
    public sendEnqueued() {
        this.batchedServerIds.forEach(serverId => {
            //console.log('sending enqueued message to server...; ', serverId, this.batchedMessagesByArea[serverId])
            this.sendToServerNoEncode(serverId, JSON.stringify(this.batchedMessagesByArea[serverId]));
            delete this.batchedMessagesByArea[serverId];
        });
        this.batchedServerIds.length = 0;
    }
    

    /*
    public enqueueToServer(protocol: any, serverId: string, system: string, type: string, payload: any, clientId) {
        if(!(serverId in this.batchedMessagesByArea)) {
            this.batchedMessagesByArea[serverId] = {};
            this.batchedServerIds.push(serverId);
        }
        if(!(this.batchedMessagesByArea[serverId][protocol][system])) {
            this.batchedMessagesByArea[serverId][protocol][system] = {};
        }
        if(!(this.batchedMessagesByArea[serverId][protocol][system][type])) {
            this.batchedMessagesByArea[serverId][protocol][system][type] = [];
        }
        this.batchedMessagesByArea[serverId][protocol][system][type].push({ payload, clientId });
  


    private getAllClientAreas(clientId: string) : Array<string> {
        return Object.keys(this.clientsByArea).filter(a => {
            this.clientsByArea[a].includes(clientId);
        })
    }
  }*/
    /**
     * Tell the gate this client joined or left an area. The gate owns the
     * client→area index (master stays orchestration-only), so this is the only
     * way it learns what to restore on a rejoin.
     */
    private reportClientAreaChange(privateId: string, areaId: string | number, joined: boolean, graceMs: number) {
      if(!this.gateServer) return;
      this.sendToServer(this.gateServer.id, [
        GATE_TO_CONNECTOR_PROTOCOLS.CLIENT_AREA_CHANGED, privateId, areaId, joined, joined ? graceMs : 0,
      ]);
    }

    private removeClientFromArea(clientId: string, areaId: string | number, opts?: { report?: boolean }) {
      const idx = this.clientsByArea[areaId]?.indexOf(clientId);
      if(idx > -1) {
        this.clientsByArea[areaId].splice(idx, 1);
        if(!this.clientsByArea[areaId].length) {
          delete this.clientsByArea[areaId]
        }
      }
      if(this.clientAreas[clientId]) {
        const areaIdx = this.clientAreas[clientId].indexOf(areaId);
        if(areaIdx > -1) {
          this.clientAreas[clientId].splice(areaIdx, 1);
          if(!this.clientAreas[clientId].length) {
            delete this.clientAreas[clientId];
          }
        }
      }

      // A deliberate leave / area-initiated removal genuinely ends membership,
      // so the gate must forget it — that is what keeps replaceClientArea and
      // leaveArea from stranding a phantom seat. Two callers pass report:false
      // instead — the DISCONNECT teardown (handleRemovedClient) and a
      // connector-closing disconnectAllClientsInArea — because in both cases
      // the membership is being HELD, not ended, and reporting would erase
      // the very list the rejoin depends on.
      if(opts?.report !== false) {
        this.reportClientAreaChange(clientId, areaId, false, 0);
      }

      // Clean up channels for this area
      if (this.areaChannels[areaId]) {
          for (const channelIndex in this.areaChannels[areaId]) {
              const channelClients = this.areaChannels[areaId][channelIndex];
              const clientIdx = channelClients.indexOf(clientId);
              if (clientIdx > -1) {
                  channelClients.splice(clientIdx, 1);
              }
          }
      }
    }

    private async requestClient(clientId: string, requestType: string | number, payload: any, timeout: number = 10000) : Promise<any> {
			const client = this.connector.clientsByPrivateId[clientId];
			if(!client) throw new Error(`Invalid client to send request to: ${clientId}`);
			return new Promise((resolve, reject) => {
				const seq = client.reqSeq++;
				const timeoutId = setTimeout(() => {
					if(client.pendingReqs[seq]) {
						delete client.pendingReqs[seq];
						reject(new Error(`Client request timed out after ${timeout}ms`));
					}
				}, timeout);
				this.connector.sendToClient(clientId, 
					[
						PROTOCOLS.CONNECTOR_REQUEST,
						seq,
						requestType,
						payload
					], 
					true,
					true
				)
				client.pendingReqs[seq] = (v: any, error?: any) => {
					clearTimeout(timeoutId);
					delete client.pendingReqs[seq];
          if(error) {
              return reject({ data: v, error });
          }
					return resolve(v);
				}
			});
    }

    private onClientReserveSeat(seat: ReservedSeat) : { gameId: GameId, gameType: GameType } {
			if(this.reservedSeats[seat.privateId]) {
				throw new Error(`Already reserved seat.`);
			}

			// Prefer a locally-known game (populated when a static area of this
			// game registered). Fall back to the type the gate resolved from the
			// master's game registry — this is what lets an instance-only game
			// (whose areas are created lazily, AFTER the seat) reserve a seat
			// before any of its areas exist on this connector.
			const gameType = (seat.gameId in this.games) ? this.games[seat.gameId].type : seat.gameType;
			if (gameType === undefined || gameType === null) {
				throw new Error(`seat.gameId: ${seat.gameId} does not exist.`)
			}
			// console.log('reserving seaat', { seat });
			this.reservedSeats[seat.privateId] = seat;

			// console.log('seat.options', seat.options)
			return { gameId: seat.gameId, gameType };
    }

    public override async onServerRequest(server: RegisteredServerData<any>, payload: any): Promise<any> {
        if(server.type === MASTER) {
            switch(payload[0]) {
                case MASTER_CONNECTOR_PROTOCOLS.PREPARE_INSTANCE_AREA: {
                    // A dynamic instance area is about to register with us — prepare
                    // (resolves later, when the area's registration lands).
                    // The master validated this instance (placed on that host, in
                    // its scope, at its address) and the descriptor carries the
                    // area's key — the area is pinned by it. Its role is AREA,
                    // whatever the payload says.
                    const areaConfig = { ...payload[1], type: AREA };
                    this.prepareForServerRegistrationRequest(areaConfig).catch(err => {
                        this.logger.error(`Connector failed preparing for instance area ${areaConfig?.id}: ${err instanceof Error ? err.message : String(err)}`);
                    });
                    return true;
                }
            }
            return;
        }
        if(server.type === GATE) {
            // console.log('GATE REQUEST', payload[0])
					switch(payload[0]) {
						case GATE_CONNECTOR_REQUEST_PROTOCOLS.RESERVE_SEAT: {
							const seat : ReservedSeat = payload[1];
							return this.onClientReserveSeat(seat);
                        }
						case GATE_CONNECTOR_REQUEST_PROTOCOLS.DISCONNECT_CLIENT: {
							// A second login for the same identity took over this session.
							// Drop the transport; removeClient's normal removed-client path
							// (handleRemovedClient) relays the DISCONNECTED leave to every
							// area this client was in — detaching the seat the taking-over
							// session is about to rebind to — and reports CLIENT_DISCONNECTED
							// to the gate, which has already retired this privateId, so that
							// report lands as a no-op. NOT a deliberate leave: the seat must
							// be held, not evicted.
							const privateId = payload[1];
							// UNCONDITIONAL, and before the early return. A reserved seat
							// admits a socket on its own: onValidateClient accepts any
							// privateId with a live `reservedSeats` entry, matching against
							// the publicId ON THAT SEAT — which is the id the taking-over
							// session adopts. Leaving the seat behind for a client that has
							// not opened its websocket YET (reservation granted, socket
							// pending) lets that socket connect afterwards and land a SECOND
							// live transport on the adopted publicId — the exact hole this
							// case exists to close, just displaced in time.
							delete this.reservedSeats[privateId];
							if(!this.connector.clientsByPrivateId[privateId]) return false;
							// removeClient also deletes the seat via handleRemovedClient.
							this.connector.removeClient(privateId);
							return true;
						}
						case GATE_CONNECTOR_REQUEST_PROTOCOLS.PEER_CONNECTION_REQUEST: {
							//console.log('PEER_CONNECTION_REQUEST', GATE_CONNECTOR_REQUEST_PROTOCOLS.PEER_CONNECTION_REQUEST)
							const requestedPrivateId = payload[1];
							const requesterPublicId = payload[2];
							const handshakeData = payload[3];
							const system = payload[4];
							const reqOpts = payload[5];
							const client = this.connector.clientsByPrivateId[requestedPrivateId];
							if(!client) return null;
							const v = await this.requestClient(client.privateId, PROTOCOLS.PEER_CONNECTION_REQUEST, [
									requesterPublicId,
									handshakeData,
									system,
									reqOpts
							]);
                            //console.log('V WAS', v);
							return v
                        }
					}
        } else if (server.type === AREA) {
          switch(payload[0]) {
            case PROTOCOLS.SERVER_TO_CLIENT_REQUEST: {

              const client = this.connector.clientsByPublicId[payload[1]];
              if(!client) {
                throw new Error(`Client can not be found.`)
              }
              if(payload.length > 4) {
                return this.requestClient(client.privateId, PROTOCOLS.SERVER_TO_CLIENT_REQUEST, [
                  payload[2], // type
                  payload[3], //data
                  server.id, // area id
                  payload[4], // toSystem
                ]);
              } else {
                return this.requestClient(client.privateId, PROTOCOLS.SERVER_TO_CLIENT_REQUEST, [
                  payload[2], // type
                  payload[3], //data
                  server.id, // area id
                ]);
              }
            }
                //[protocol, area,  type, data, toSystem,]
            case PROTOCOLS.AREA_REQUEST_RELAY: {
              // Incoming: [AREA_REQUEST_RELAY, toAreaId, type, data, to?] — the
              // sender OMITTED the trailing `to` slot for a game-hook request;
              // preserve that on every re-forward (never send null/undefined
              // over the wire).
              if(payload[1] in this.serverConnections) {
                const relay: ProtocolArray = [payload[0], server.id, payload[2], payload[3]];
                if (payload[4] !== undefined) relay.push(payload[4]);
                return this.requestServer(payload[1], relay)
              } else if (this.masterServer) {
                // Outgoing: [AREA_RELAY, toAreaId, fromAreaId, type, data, to?]
                const relay: ProtocolArray = [CONNECTOR_MASTER_REQUEST_PROTOCOLS.AREA_RELAY, payload[1], server.id, payload[2], payload[3]];
                if (payload[4] !== undefined) relay.push(payload[4]);
                return this.requestServer(this.masterServer.id, relay)
              }
            }
          }
        }
    }

    private handleClientJoinedArea(
        areaId: string | number,
        clientPublicId: string,
        options: any,
        areaData: any,
        areaState: any,
        preventMultiClientAreas: boolean,
        reconnectGraceMs?: number
    ) {
      const client = this.connector.clientsByPublicId[clientPublicId];
      const clientId = client.privateId;

      if(!this.clientsByArea[areaId]?.includes(clientId)) {
        if(!this.clientsByArea[areaId]) {
          this.clientsByArea[areaId] = [];
        }
        this.clientsByArea[areaId].push(clientId)
      }

      if(!this.clientAreas[clientId]?.includes(areaId)) {
        if(!this.clientAreas[clientId]) {
          this.clientAreas[clientId] = [];
        }
        this.clientAreas[clientId].push(areaId);
        this.reportClientAreaChange(clientId, areaId, true, reconnectGraceMs ?? 0);
      }

      if(preventMultiClientAreas) {
        const allAreas = [...this.clientAreas[clientId]]
        allAreas.forEach(a => {
          if(a !== areaId) {
            this.removeClientFromArea(clientId, a);
            this.sendToServer(a, [
              PROTOCOLS.CLIENT_LEFT_AREA,
              client.publicId, 
              LEAVE_AREA_REASONS.SWITCHED_AREA,
              areaId
            ])
          }
        })
      }

      /*
      console.log('SENDING TO CLIENT',
            PROTOCOLS.CLIENT_JOINED_AREA, 
            this.serverConnections[areaId].id,
            options, 
            {preventMultiClientAreas, areaData, options }
      );
      */
      this.connector.sendToClient(
        clientId, 
        [
          PROTOCOLS.CLIENT_JOINED_AREA,
          areaId,
          this.connectedAreas[areaId]?.options?.areaFile?.type,
          options,
          areaData,
          areaState,
          preventMultiClientAreas
        ],
        true, 
        true
      ) 
    }

    handleSystemStateSync(areaId: string, message: string) {
        const clientIds = this.clientsByArea[areaId];
        if(!clientIds) {
            return;
        }
    }

    public override onServerRelayByAreaMessage(server: BaseServerConfig<any>, payload: string) {
        const clients = this.clientsByArea[server.id];
        if(clients) {
            this.connector.sendToClients(clients, payload, true);
        }
    }

    override onServerClientRelayMessage(server: BaseServerConfig<any>, clientId: string, payload: string) {
       this.connector.clientsByPublicId[clientId]?.send(payload, true)
    }

    override onServerClientsRelayMessage(server: BaseServerConfig<any>, clientIds: string[], payload: string) {
        this.connector.sendToClientsByPublicIds(clientIds || [], payload, true)
    }

    override onServerChannelRelayMessage(server: BaseServerConfig<any>, channelIndex: number, payload: string) {
        const clients = this.areaChannels[server.id]?.[channelIndex];
        if (clients && clients.length) {
            this.connector.sendToClients(clients, payload);
        }
    }

    override onServerRawRelayMessage(server: BaseServerConfig<any>, payload: string) {
        this.sendRawToAreaClients(server.id, payload);
    }

    onAreaServerMessage(serverOpts: BaseServerConfig<any>, message: any) {
        const protocol : PROTOCOLS = message[0];
        switch(protocol) {
            case PROTOCOLS.SYSTEM_STATE_SYNC:
                this.connector.sendToClients(this.clientsByArea[serverOpts.id] || [], message);
                break;
    
            case PROTOCOLS.CLIENT_JOINED_AREA: {
                /*todo: should be a relayed request incase the area does not allow the client to join, we
                // want to let the area making the request know that.
                // ie psuedo code:
                // const joined = await this.requestServer(message)
                // if (joined) {handleClientJoinedArea()}
                // else throw new Error('Client was not allowed to join area.')
                */
                // message[6] is the error slot EXCLUSIVELY (see
                // AreaServer.handleClientJoin) — a successful join always leaves
                // it undefined, so a plain truthy read is reliable. Do not
                // repurpose this slot: reconnectGraceMs lives at message[7]
                // instead, one past it, specifically so this stays a
                // one-index/one-meaning read.
                const joinError = message[6];
                if (joinError) {
                    // The area could not admit this client. Relay a client-facing
                    // join-error frame — the same shape as the REQUEST_JOIN_AREA
                    // reject above, error in the slot the client parses as msg[7]
                    // — so the client's handleJoinedArea surfaces the failure. No
                    // membership bookkeeping: the client was never added here.
                    // NOTE: a HELD rejoin can no longer land here — a seat the
                    // area holds rebinds unconditionally, without consulting
                    // onJoinRequest (AreaServer.handleReconnectTransportJoin).
                    const rejectedClient = this.connector.clientsByPublicId[message[1]];
                    if (rejectedClient) {
                        this.connector.sendToClient(rejectedClient.privateId, [
                            PROTOCOLS.CLIENT_JOINED_AREA,
                            serverOpts.id, null, null, null, null, false,
                            joinError,
                        ], true, true);
                    }
                    break;
                }
                this.handleClientJoinedArea(
                    serverOpts.id,
                    message[1],
                    message[2],
                    message[3],
                    message[4],
                    //preventMultiClientAreas
                    message[5],
                    //reconnectGraceMs (message[6] is reserved for the error slot — see above)
                    message[7]
                );
                break;
            };
            case PROTOCOLS.CLIENT_LEFT_AREA: {
              const client = this.connector.clientsByPublicId[message[1]];
              if(!client) {
                return;
              }
              this.removeClientFromArea(client.privateId, serverOpts.id);
              this.connector.sendToClient(client.privateId,
                [
                  PROTOCOLS.CLIENT_LEFT_AREA,
                  serverOpts.id,
                  LEAVE_AREA_REASONS.REMOVED_BY_AREA, // assumed reason since it came from the area.
                  message[2],
                ])
                break;
            };
            case PROTOCOLS.AREA_RELAY: {
                // Incoming: [AREA_RELAY, toAreaId, type, data, to?] — the sender
                // OMITTED the trailing `to` slot for a game-hook message; preserve
                // that on every re-forward (never send null/undefined over the wire).
                if(message[1] in this.serverConnections) {
                    // Outgoing: [AREA_RELAY, fromAreaId, type, data, to?]
                    const relay: ProtocolArray = [PROTOCOLS.AREA_RELAY, serverOpts.id, message[2], message[3]];
                    if (message[4] !== undefined) relay.push(message[4]);
                    return this.sendToServer(message[1], relay)
                } else if (this.masterServer) {
                    // Outgoing: [AREA_RELAY, toAreaId, fromAreaId, type, data, to?]
                    const relay: ProtocolArray = [CONNECTOR_MASTER_REQUEST_PROTOCOLS.AREA_RELAY, message[1], serverOpts.id, message[2], message[3]];
                    if (message[4] !== undefined) relay.push(message[4]);
                    return this.sendToServer(this.masterServer.id, relay)
                }
                break;
            }
            case PROTOCOLS.AREA_STATE_SYNC: {
                const clients = this.clientsByArea[serverOpts.id] || [];
                if(clients?.length) {
                    this.connector.sendToClients(
                        clients,
                        JSON.stringify([PROTOCOLS.AREA_STATE_SYNC, serverOpts.id, message[1]])
                   );
                }
                break;
            }
            case PROTOCOLS.AREA_RELAYS: {
                break;
            }
            case PROTOCOLS.CLIENT_TO_SERVER_REQUEST:
            case PROTOCOLS.SERVER_TO_CLIENT_MESSAGE_INSTANT: {
                const clientPubId = message[1];
                const client = this.connector.clientsByPrivateId[clientPubId];
                if(!client) {
                  return;
                }
                const clientId = client?.privateId;
                const type = message[2];
                const payload = message[3];
                if(message.length > 4) {
                  // console.log('got server to client message', { clientId, system, type, payload })
                  this.connector.sendToClient(clientId, [
                    protocol, 
                    serverOpts.id, 
                    type, 
                    payload, 
                    message[4] // toSystem if exists
                  ])
                } else {
                    this.connector.sendToClient(clientId, [
                        protocol, 
                        serverOpts.id, 
                        type, 
                        payload, 
                    ])
                }
                break;
            }
      case PROTOCOLS.SERVER_TO_CLIENT_RAW_MESSAGE: {
        const encoded = message[1];
        const targetPrivateIds = message[2] as string[] | undefined;
        this.sendRawToAreaClients(serverOpts.id, encoded, targetPrivateIds);
        break;
      }
            //todo: should be a request incase the area does not allow the client to join.
            case PROTOCOLS.AREA_TO_CONNECTOR_ADD_TO_CHANNEL: {
                const clientPublicId = message[1];
                const channelIndex = message[2];
                const areaId = serverOpts.id;
                const client = this.connector.clientsByPublicId[clientPublicId];
                if (client) {
                    if (!this.areaChannels[areaId]) {
                        this.areaChannels[areaId] = {};
                    }
                    if (!this.areaChannels[areaId][channelIndex]) {
                        this.areaChannels[areaId][channelIndex] = [];
                    }
                    if (!this.areaChannels[areaId][channelIndex].includes(client.privateId)) {
                        this.areaChannels[areaId][channelIndex].push(client.privateId);
                    }
                }
                break;
            }
            case PROTOCOLS.AREA_TO_CONNECTOR_REMOVE_FROM_CHANNEL: {
                const clientPublicId = message[1];
                const channelIndex = message[2];
                const areaId = serverOpts.id;
                const client = this.connector.clientsByPublicId[clientPublicId];
                if (client && this.areaChannels[areaId] && this.areaChannels[areaId][channelIndex]) {
                    const idx = this.areaChannels[areaId][channelIndex].indexOf(client.privateId);
                    if (idx > -1) {
                        this.areaChannels[areaId][channelIndex].splice(idx, 1);
                    }
                }
                break;
            }
            case PROTOCOLS.AREA_TO_CONNECTOR_CHANNEL_MESSAGE: {
                const channelIndex = message[1];
                const payload = message[2];
                const areaId = serverOpts.id;
                const clients = this.areaChannels[areaId]?.[channelIndex];
                if (clients && clients.length) {
                    this.connector.sendToClients(clients, payload);
                }
                break;
            }
            case PROTOCOLS.AREA_TO_CONNECTOR_CHANNEL_REQUEST: {
              const channelIndex = message[1];
              const reqType = message[2];
              const reqData = message[3];
              const toSystem = message[4];
              const correlationId = message[5];
              const areaId = serverOpts.id;

              const clients = this.areaChannels[areaId]?.[channelIndex];
              if (!clients || !clients.length) {
                break;
              }

              clients.forEach(async (privateId) => {
                const client = this.connector.clientsByPrivateId[privateId];
                if (!client) {
                  this.sendToServer(areaId, [
                    PROTOCOLS.CONNECTOR_TO_AREA_CHANNEL_RESPONSE,
                    correlationId,
                    privateId,
                    false,
                    null,
                    'Client not found'
                  ]);
                  return;
                }

                try {
                  const response = await this.requestClient(privateId, PROTOCOLS.SERVER_TO_CLIENT_REQUEST, [
                    reqType,
                    reqData,
                    areaId,
                    toSystem
                  ]);
                  this.sendToServer(areaId, [
                    PROTOCOLS.CONNECTOR_TO_AREA_CHANNEL_RESPONSE,
                    correlationId,
                    client.publicId,
                    true,
                    response
                  ]);
                } catch (error: any) {
                  this.sendToServer(areaId, [
                    PROTOCOLS.CONNECTOR_TO_AREA_CHANNEL_RESPONSE,
                    correlationId,
                    client.publicId,
                    false,
                    null,
                    error?.message || error
                  ]);
                }
              });
              break;
            }
            case PROTOCOLS.AREA_TO_CONNECTOR_DESTROY_CHANNEL: {
                const channelIndex = message[1];
                const areaId = serverOpts.id;
                if (this.areaChannels[areaId] && this.areaChannels[areaId][channelIndex]) {
                    delete this.areaChannels[areaId][channelIndex];
                }
                break;
            }
            case PROTOCOLS.CLIENT_ADDED_TO_AREA_BY_AREA:
                if(message[2] in this.serverConnections) {
                    const client = this.connector.clientsByPublicId[message[1]];
                      // protocol, clientId, areaId, opts
                    if(client) {
                        //todo: should be a request incase the area does not allow the client to join.
                        this.sendToServer(message[2], [
                            PROTOCOLS.CLIENT_JOINED_AREA,
                            client.publicId,
                            client.seat.auth,
                            message[3]
                        ]);
                    }
                } else if (this.masterServer) {
                    // areaId, connectorId, to, type, data                        
                    return this.requestServer(this.masterServer.id, [CONNECTOR_MASTER_REQUEST_PROTOCOLS.AREA_RELAY, message[1], serverOpts.id, message[2], message[3], message[4]])
                }      
        }
    }

    override onServerMessage(serverOpts: BaseServerConfig<any>, message: any) {
        if(serverOpts.type === AREA) {
            this.onAreaServerMessage(serverOpts, message);
        } else if (serverOpts.type === MASTER) {
            switch(message[0]) {
                case MASTER_CONNECTOR_PROTOCOLS.REMOVE_INSTANCE_AREA: {
                    // Torn-down dynamic instance — drop the area connection now (fires
                    // the AREA close cleanup: clientsByArea, channels, client notices).
                    const areaConnection = this.serverConnections[message[1]];
                    if (areaConnection) {
                        this.disconnectServer(areaConnection).catch(err => {
                            this.logger.error(`Connector failed dropping instance area ${message[1]}: ${err instanceof Error ? err.message : String(err)}`);
                        });
                    }
                    break;
                }
                case MASTER_CONNECTOR_PROTOCOLS.ASSET_CHANGED:
                    this.broadcastAssetChangeToClients(message[1], message[2]);
                    break;
            }
        } else if (serverOpts.type === GATE) {
            const protocol : GATE_TO_CONNECTOR_PROTOCOLS = message[0];

            switch(protocol) {
                case GATE_TO_CONNECTOR_PROTOCOLS.REDIRECT_MESSAGE_TO_CLIENT: {
                    const protocol2 = message[1];
                    const clientId = message[2];
                    const data = message[3];
                    this.connector.sendToClient(clientId, [protocol2, data], true, true)
                }
            }
        }
    }

  private sendRawToAreaClients(areaId: string | number, encodedPayload: string, targetPrivateIds?: string[]) {
    const clients = targetPrivateIds?.length ? targetPrivateIds : this.clientsByArea[areaId] || [];
    if(!clients?.length) {
      return;
    }
    for(let i = 0; i < clients.length; i++) {
      this.connector.sendRawToClient(clients[i], encodedPayload);
    }
  }
}